import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

/**
 * Default slice size for blob conversion
 */
const BLOB_SLICE_SIZE: number = 512;

/**
 * Position offset for extracting file extension from content type
 */
const EXTENSION_OFFSET: number = 1;

@Injectable({
  providedIn: 'root',
})
export class PdfAndImageService {
  constructor() {
    // Service initialization
  }

  /**
   * Converts various data types to a base64 string for internal processing.
   */
  private toBase64(data: string | Uint8Array | Blob | ArrayBuffer): string {
    if (typeof data === 'string') {
      return data;
    }
    if (data instanceof Uint8Array || data instanceof ArrayBuffer) {
      const bytes = data instanceof ArrayBuffer ? new Uint8Array(data) : data;
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return btoa(binary);
    }
    // For Blob, return empty string - callers should handle async conversion
    return '';
  }

  /**
   * Creates a Blob URL from data that may be a string, Uint8Array, or Blob.
   */
  private toBlobDirect(data: string | Uint8Array | Blob | ArrayBuffer, contentType: string): Blob {
    if (data instanceof Blob) {
      return data;
    }
    if (data instanceof Uint8Array || data instanceof ArrayBuffer) {
      return new Blob([data], { type: contentType });
    }
    // string = base64
    return this.getBase64AsBlob(data, contentType);
  }

  public getBase64AsBlob(data: string, contentType: string): Blob {
    const sliceSize: number = BLOB_SLICE_SIZE;
    const byteCharacters: string = atob(data);
    const byteArrays: Uint8Array[] = [];

    for (let offset: number = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice: string = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers: number[] = new Array(slice.length);
      for (let i: number = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray: Uint8Array = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  public getBlobUrl(base64Data: string, contentType: string): Observable<string> {
    const blob: Blob = this.getBase64AsBlob(base64Data, contentType);
    const blobUrl: string = URL.createObjectURL(blob);
    return of(blobUrl);
  }

  public view(data: string | Uint8Array | Blob | ArrayBuffer, contentType: string): void {
    const blob: Blob = this.toBlobDirect(data, contentType);
    const url: string = URL.createObjectURL(blob);
    if (url) {
      window.open(url, '_blank');
    }
  }

  public download(data: string | Uint8Array | Blob | ArrayBuffer, contentType: string, fileName: string): void {
    const blob: Blob = this.toBlobDirect(data, contentType);
    const a: HTMLAnchorElement = document.createElement('a');
    document.body.appendChild(a);
    const url: string = window.URL.createObjectURL(blob);
    a.href = url;
    const fileType: string[] = contentType.split('/');
    a.download = String(fileName + (fileType[EXTENSION_OFFSET] ? '.' + fileType[EXTENSION_OFFSET] : ''));
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  openHTMLInNewTab(htmlString: string, cssString: string): void {
    const fixedHtml = htmlString.replace(
      /src="\/Logos\//g,
      `src="${window.location.origin}/assets/Logos/`
    ).replace(
      /src="\/Language\//g,
      `src="${window.location.origin}/assets/Language/`
    );

    const fullHtml: string = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <link rel="stylesheet" href="${window.location.origin}/assets/external-css/sesis.css">
      <link rel="stylesheet" href="${window.location.origin}/assets/external-css/doe_templates.css">
      ${cssString || ''}
    </head>
    <body>
      <div class="DOCCONTENT">
        ${fixedHtml || ''}
      </div>
    </body>
    </html>
  `;

    const blob: Blob = new Blob([fullHtml], { type: 'text/html' });
    const url: string = URL.createObjectURL(blob);

    window.open(url, '_blank');
  }


  public print(data: string | Uint8Array | Blob | ArrayBuffer, contentType: string): void {
    const blob: Blob = this.toBlobDirect(data, contentType);
    const url: string = window.URL.createObjectURL(blob);
    const printWindow: Window | null = window.open(url, '_blank');
    if (printWindow) {
      printWindow.onload = function () {
        printWindow?.print();
      };
    }
  }

  private b64toBlob(b64Data: string, contentType: string): Blob {
    contentType = contentType || '';
    const sliceSize: number = BLOB_SLICE_SIZE;

    const byteCharacters: string = atob(b64Data);
    const byteArrays: Uint8Array[] = [];

    for (let offset: number = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice: string = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers: number[] = new Array(slice.length);
      for (let i: number = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray: Uint8Array = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    const blob: Blob = new Blob(byteArrays, { type: contentType });
    return blob;
  }
}